import { api } from '@/lib/api';
import type {
  ChangePinPayload,
  CreateUserPayload,
  CreatedUserResponse,
  ResetPinResponse,
  UpdateUserPayload,
  UserRecord
} from '@/types/api';

export const usersApi = {
  list: () => api.get<UserRecord[]>('/users'),
  create: (payload: CreateUserPayload) => api.post<CreatedUserResponse>('/users', payload),
  update: (id: string, payload: UpdateUserPayload) =>
    api.patch<UserRecord>(`/users/${id}`, payload),
  // V5 — self-update endpoint that any authenticated user can call to edit
  // their own profile (name, phone, email). Backend strips out role/view_type
  // so users can't elevate themselves.
  updateMyProfile: (payload: Partial<UpdateUserPayload>) =>
    api.patch<UserRecord>('/users/me', payload),
  resetPin: (id: string) => api.post<ResetPinResponse>(`/users/${id}/reset-pin`),
  // V4.5d — Soft-delete user. Returns success + message; the user row is
  // marked deleted_at and inactive, all their refresh tokens revoked.
  remove: (id: string) =>
    api.delete<{
      success: true;
      user_id: string;
      alias: string;
      message: string;
    }>(`/users/${id}`),
  // V4.9.18 — List soft-deleted users (for the Restore feature). Gated by the
  // same users.delete capability as remove(). Returns deleted accounts only.
  listDeleted: () => api.get<UserRecord[]>('/users/deleted'),
  // V4.9.18 — Restore (un-delete) a soft-deleted user: clears deleted_at and
  // re-activates them so they can log in and receive assignments again.
  restore: (id: string) =>
    api.post<{
      success: true;
      user_id: string;
      alias: string;
      message: string;
    }>(`/users/${id}/restore`),
  changeOwnPin: (payload: ChangePinPayload) =>
    api.post<{ success: true }>(`/users/me/change-pin`, payload)
};
